import { http } from './http';
import { withOrgTenant } from './organizations.service';
import { unwrapData, unwrapPaginated } from '../utils/unwrapApiData';

const dataOf = (payload) => unwrapData(payload) ?? payload;

function withOptionalOrg(organizationId, extra = {}) {
  return organizationId ? withOrgTenant(organizationId, extra) : extra;
}

export async function listCatalog(params = {}) {
  const { data } = await http.get('/catalog/packages', { params });
  return dataOf(data) || [];
}

export async function listOrganizationCatalog() {
  const { data } = await http.get('/commerce/catalog');
  return dataOf(data) || [];
}

export async function getCurrentCart() {
  const { data } = await http.get('/commerce/carts/current');
  return dataOf(data);
}

export async function putCartItem(cartId, priceId, quantity = 1) {
  const { data } = await http.post(`/commerce/carts/${cartId}/items`, { price_id: priceId, quantity });
  return dataOf(data);
}

export async function removeCartItem(cartId, itemId) {
  const { data } = await http.delete(`/commerce/carts/${cartId}/items/${itemId}`);
  return dataOf(data);
}

export async function applyCartCoupon(cartId, code) {
  const { data } = await http.put(`/commerce/carts/${cartId}/coupon`, { code: code || null });
  return dataOf(data);
}

export async function quoteCart(cartId, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post(`/commerce/carts/${cartId}/quote`, {}, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function checkoutQuote(quoteId, body, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post(`/commerce/quotes/${quoteId}/checkout`, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function getBillingProfile() {
  const { data } = await http.get('/commerce/billing/profile');
  return dataOf(data);
}

export async function updateBillingProfile(body) {
  const { data } = await http.put('/commerce/billing/profile', body);
  return dataOf(data);
}

export async function getUsageForecast() {
  const { data } = await http.get('/commerce/usage-forecast');
  return dataOf(data);
}

export async function listBillingInvoices(params = {}) {
  const { data } = await http.get('/commerce/billing/invoices', { params });
  return unwrapPaginated(data);
}

export async function listBillingPayments(params = {}) {
  const { data } = await http.get('/commerce/billing/payments', { params });
  return unwrapPaginated(data);
}

export async function listBillingHistory(params = {}) {
  const { data } = await http.get('/commerce/billing/change-history', { params });
  return unwrapPaginated(data);
}

export async function getBillingInvoice(id) {
  const { data } = await http.get(`/commerce/billing/invoices/${id}`);
  return dataOf(data);
}

async function parseBlobError(error) {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const payload = JSON.parse(await data.text());
      error.response.data = payload;
    } catch {
      /* keep blob */
    }
  }
  return error;
}

export async function downloadBillingInvoice(id) {
  try {
    const response = await http.get(`/commerce/billing/invoices/${id}/download`, { responseType: 'blob' });
    const contentType = String(response.headers?.['content-type'] || '');
    if (contentType.includes('application/json')) {
      const payload = JSON.parse(await response.data.text());
      const err = new Error(payload?.message || 'دانلود فاکتور ناموفق بود.');
      err.response = { data: payload };
      throw err;
    }
    const disposition = response.headers?.['content-disposition'] || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] || `invoice-${id}.pdf`;
    return { blob: response.data, filename };
  } catch (error) {
    throw await parseBlobError(error);
  }
}

export async function payBillingInvoice(id, body, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post(`/commerce/billing/invoices/${id}/pay`, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function sendBillingInvoice(id, email) {
  const { data } = await http.post(`/commerce/billing/invoices/${id}/send`, email ? { email } : {});
  return dataOf(data);
}

export async function requestSalesConsultation(body) {
  const { data } = await http.post('/commerce/billing/sales-request', body);
  return dataOf(data);
}

export async function listPlatformSalesRequests(params = {}) {
  const { data } = await http.get('/platform/commerce/sales-requests', { params });
  return unwrapPaginated(data);
}

export async function updatePlatformSalesRequest(requestId, body) {
  const { data } = await http.patch(`/platform/commerce/sales-requests/${requestId}`, body);
  return dataOf(data);
}

export async function uploadPaymentEvidence(paymentId, formData) {
  const { data } = await http.post(`/commerce/payments/${paymentId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return dataOf(data);
}

export async function previewRenewal(body = {}) {
  const { data } = await http.post('/commerce/subscription/renewal/preview', body);
  return dataOf(data);
}

export async function renewSubscription(body, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post('/commerce/subscription/renewal', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function getAutoRenew() {
  const { data } = await http.get('/commerce/subscription/auto-renew');
  return dataOf(data);
}

export async function setAutoRenew(enabled) {
  const { data } = await http.put('/commerce/subscription/auto-renew', { enabled });
  return dataOf(data);
}

export async function resumeSubscription() {
  const { data } = await http.post('/commerce/subscription/resume');
  return dataOf(data);
}

export async function adviseAddonUpgrade(items) {
  const { data } = await http.post('/commerce/addons/upgrade-advice', { items });
  return dataOf(data);
}

export async function checkout(body, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post('/commerce/checkout', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function getCurrentPlan() {
  const { data } = await http.get('/commerce/change-plan/current');
  return dataOf(data);
}

export async function previewChangePlan(priceId) {
  const { data } = await http.get('/commerce/change-plan/preview', { params: { price_id: priceId } });
  return dataOf(data);
}

export async function applyChangePlan(body, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post('/commerce/change-plan', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function cancelPendingPlanChange() {
  const { data } = await http.delete('/commerce/change-plan/pending');
  return dataOf(data);
}

export async function getBillingSummary(organizationId) {
  const { data } = await http.get('/commerce/billing-summary', withOptionalOrg(organizationId));
  return dataOf(data);
}

export async function getOrganizationCapabilities(organizationId) {
  const { data } = await http.get('/organization/capabilities', withOptionalOrg(organizationId));
  return dataOf(data);
}

export async function listContracts(params = {}, organizationId) {
  const { data } = await http.get('/commerce/contracts', withOptionalOrg(organizationId, { params }));
  return unwrapPaginated(data);
}

export async function cancelContractAtPeriodEnd(id) {
  const { data } = await http.post(`/commerce/contracts/${id}/cancel-at-period-end`);
  return dataOf(data);
}

export async function listEntitlementPools(params = {}, organizationId) {
  const { data } = await http.get('/commerce/entitlement-pools', withOptionalOrg(organizationId, { params }));
  return unwrapPaginated(data);
}

export async function listUsageEvents(params = {}, organizationId) {
  const { data } = await http.get('/commerce/usage-events', withOptionalOrg(organizationId, { params }));
  return unwrapPaginated(data);
}

export async function listOrders(params = {}, organizationId) {
  const { data } = await http.get('/commerce/orders', withOptionalOrg(organizationId, { params }));
  return unwrapPaginated(data);
}

export async function listPlatformPackages(params = {}) {
  const { data } = await http.get('/platform/commerce/packages', { params });
  return dataOf(data) || [];
}

export async function createPlatformPackage(body) {
  const { data } = await http.post('/platform/commerce/packages', body);
  return dataOf(data);
}

export async function updatePlatformPackage(productId, body) {
  const { data } = await http.put(`/platform/commerce/packages/${productId}`, body);
  return dataOf(data);
}

export async function createPlatformVersion(productId, body) {
  const { data } = await http.post(`/platform/commerce/packages/${productId}/versions`, body);
  return dataOf(data);
}

export async function updatePlatformVersion(versionId, body) {
  const { data } = await http.put(`/platform/commerce/versions/${versionId}`, body);
  return dataOf(data);
}

export async function publishPlatformVersion(versionId) {
  const { data } = await http.post(`/platform/commerce/versions/${versionId}/publish`);
  return dataOf(data);
}

export async function archivePlatformPackage(productId) {
  const { data } = await http.post(`/platform/commerce/packages/${productId}/archive`);
  return dataOf(data);
}

export async function deletePlatformPackage(productId) {
  const { data } = await http.delete(`/platform/commerce/packages/${productId}`);
  return dataOf(data);
}

export async function grantOrganizationPackage(body) {
  const { data } = await http.post('/platform/commerce/grants', body);
  return dataOf(data);
}

export async function getPlatformOverview(params = {}) {
  const { data } = await http.get('/platform/commerce/overview', { params });
  return dataOf(data);
}

export async function getRevenueReport(params = {}) {
  const { data } = await http.get('/platform/commerce/revenue', { params });
  return dataOf(data);
}

export async function getAiEconomics(params = {}) {
  const { data } = await http.get('/platform/commerce/ai-economics', { params });
  return dataOf(data);
}

export async function getOrganizationUsageLedger(organizationId, params = {}) {
  const { data } = await http.get(`/platform/commerce/organizations/${organizationId}/usage-ledger`, { params });
  return unwrapPaginated(data);
}

export async function getPlanPerformanceReport(params = {}) {
  const { data } = await http.get('/platform/commerce/plan-performance', { params });
  return dataOf(data);
}

export async function getCommerceResources() {
  const { data } = await http.get('/platform/commerce/resources');
  return dataOf(data);
}

export async function createCommerceResource(body) {
  const { data } = await http.post('/platform/commerce/resources', body);
  return dataOf(data);
}

export async function createCommerceRate(resourceId, body, stepUpToken) {
  const { data } = await http.post(`/platform/commerce/resources/${resourceId}/rates`, body, {
    headers: { 'X-2FA-Step-Up-Token': stepUpToken },
  });
  return dataOf(data);
}

export async function reviewManualPayment(paymentId, action, body, stepUpToken) {
  const { data } = await http.post(`/platform/commerce/manual-payments/${paymentId}/${action}`, body, {
    headers: { 'X-2FA-Step-Up-Token': stepUpToken },
  });
  return dataOf(data);
}

export async function recordRefund(paymentId, body, stepUpToken, idempotencyKey = crypto.randomUUID()) {
  const { data } = await http.post(`/platform/commerce/payments/${paymentId}/refunds`, body, {
    headers: { 'X-2FA-Step-Up-Token': stepUpToken, 'Idempotency-Key': idempotencyKey },
  });
  return dataOf(data);
}

export async function listPlatformOrders(params = {}) {
  const { data } = await http.get('/platform/commerce/orders', { params });
  return unwrapPaginated(data);
}

export async function cancelPlatformOrder(orderId) {
  const { data } = await http.post(`/platform/commerce/orders/${orderId}/cancel`);
  return dataOf(data);
}

export async function confirmPlatformOrderManualPayment(orderId, { note, file, referenceId } = {}) {
  const form = new FormData();
  if (note) form.append('note', note);
  if (referenceId) form.append('reference_id', referenceId);
  if (file) form.append('file', file);
  const { data } = await http.post(`/platform/commerce/orders/${orderId}/manual-pay`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return dataOf(data);
}

export async function listPaymentAttempts(params = {}) {
  const { data } = await http.get('/platform/commerce/payment-attempts', { params });
  return unwrapPaginated(data);
}

export async function listPlatformInvoices(params = {}) {
  const { data } = await http.get('/platform/commerce/invoices', { params });
  return unwrapPaginated(data);
}

export async function sendPlatformInvoice(invoiceId, body = {}) {
  const { data } = await http.post(`/platform/commerce/invoices/${invoiceId}/send`, body);
  return dataOf(data);
}

export async function listPlatformPayments(params = {}) {
  const { data } = await http.get('/platform/commerce/payments', { params });
  return unwrapPaginated(data);
}

export async function listPlatformSubscriptions(params = {}) {
  const { data } = await http.get('/platform/commerce/subscriptions', { params });
  return unwrapPaginated(data);
}

export async function suspendPlatformContract(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/suspend`);
  return dataOf(data);
}

export async function unsuspendPlatformContract(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/unsuspend`);
  return dataOf(data);
}

export async function listPlatformRenewals(params = {}) {
  const { data } = await http.get('/platform/commerce/renewals', { params });
  return unwrapPaginated(data);
}

export async function sendRenewalReminder(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/reminder`);
  return dataOf(data);
}

export async function createRenewalDraft(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/renewal-draft`);
  return dataOf(data);
}

export async function previewPlatformRenewal(contractId) {
  const { data } = await http.get(`/platform/commerce/contracts/${contractId}/renewal-preview`);
  return dataOf(data);
}

export async function applyPlatformRenewal(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/renew`);
  return dataOf(data);
}

export async function downloadPlatformInvoice(invoiceId) {
  const { data } = await http.get(`/platform/commerce/invoices/${invoiceId}/download`);
  return dataOf(data);
}

export async function requestSalesContact(contractId) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/sales-contact`);
  return dataOf(data);
}

export async function offerRenewalDiscount(contractId, body) {
  const { data } = await http.post(`/platform/commerce/contracts/${contractId}/renewal-discount`, body);
  return dataOf(data);
}

export async function listUpgradeCandidates() {
  const { data } = await http.get('/platform/commerce/upgrade-candidates');
  return dataOf(data) || [];
}

export async function listDiscountCodes(params = {}) {
  const { data } = await http.get('/platform/commerce/discount-codes', { params });
  return unwrapPaginated(data);
}

export async function createDiscountCode(body) {
  const { data } = await http.post('/platform/commerce/discount-codes', body);
  return dataOf(data);
}

export async function toggleDiscountCode(discountId) {
  const { data } = await http.post(`/platform/commerce/discount-codes/${discountId}/toggle`);
  return dataOf(data);
}

export async function listJournals(params = {}) {
  const { data } = await http.get('/platform/commerce/journals', { params });
  return unwrapPaginated(data);
}

export async function publishCurrentTaxRate(body) {
  const { data } = await http.post('/platform/commerce/tax-rates/current', body);
  return dataOf(data);
}

export async function getCurrentTaxRate() {
  const { data } = await http.get('/platform/commerce/tax-rates/current');
  return dataOf(data);
}

export async function getPaymentSettings() {
  const { data } = await http.get('/platform/commerce/payment-settings');
  return dataOf(data);
}

export async function savePaymentSettings(body) {
  const { data } = await http.put('/platform/commerce/payment-settings', body);
  return dataOf(data);
}

export async function listTaxRates(params = {}) {
  const { data } = await http.get('/platform/commerce/tax-rates', { params });
  return unwrapPaginated(data);
}

export async function createTaxRate(body, stepUpToken) {
  const { data } = await http.post('/platform/commerce/tax-rates', body, {
    headers: { 'X-2FA-Step-Up-Token': stepUpToken },
  });
  return dataOf(data);
}

function filenameFromDisposition(header, fallback) {
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(String(header || ''));
  const raw = match?.[1] || match?.[2];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

async function downloadCommerceExport(path, params, fallback) {
  const response = await http.get(path, { params, responseType: 'blob' });
  return {
    blob: response.data,
    filename: filenameFromDisposition(response.headers?.['content-disposition'], fallback),
  };
}

export async function exportPlatformRevenue(params = {}) {
  const format = params.format || 'xlsx';
  return downloadCommerceExport(
    '/platform/commerce/revenue/export',
    params,
    `safemeet-revenue.${format}`,
  );
}

export async function exportPlatformOrders(params = {}) {
  const format = params.format || 'xlsx';
  return downloadCommerceExport(
    '/platform/commerce/orders/export',
    params,
    `safemeet-orders.${format}`,
  );
}

export async function listPlatformProposals(params = {}) {
  const { data } = await http.get('/platform/commerce/proposals', { params });
  return dataOf(data) || [];
}

export async function createPlatformProposal(body) {
  const { data } = await http.post('/platform/commerce/proposals', body);
  return dataOf(data);
}

export async function updatePlatformProposal(proposalId, body) {
  const { data } = await http.put(`/platform/commerce/proposals/${proposalId}`, body);
  return dataOf(data);
}

export async function sendPlatformProposal(proposalId) {
  const { data } = await http.post(`/platform/commerce/proposals/${proposalId}/send`);
  return dataOf(data);
}

export async function getPlatformOrgCurrentPlan(organizationId) {
  const { data } = await http.get('/platform/commerce/proposals/current-plan', {
    params: { organization_id: organizationId },
  });
  return dataOf(data);
}

export async function activatePlatformProposalDirect(body) {
  const { data } = await http.post('/platform/commerce/proposals/activate-direct', body);
  return dataOf(data);
}

export async function listOrganizationProposals() {
  const { data } = await http.get('/commerce/proposals');
  return dataOf(data) || [];
}
